import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { getAccounts } from "../services/crmAccountsService";
import {
  createOpportunity,
  getPipelineStages,
} from "../services/crmOpportunitiesService";
import useCrmPermissions from "../hooks/useCrmPermissions";
import "../styles/crm.css";

const CreateOpportunityPage = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const { canCreateRecords } = useCrmPermissions();

  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState({
    account_id: "",
    primary_contact_id: "",
    name: "",
    amount: "",
    probability: "",
    expected_close_date: "",
    next_step: "",
    description: "",
    forecast_category: "pipeline",
    currency_code: "NGN",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!companyId) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const [accountRows, stageRows, contactsRes] = await Promise.all([
        getAccounts(companyId),
        getPipelineStages(companyId),
        supabase
          .from("crm_contacts")
          .select("*")
          .eq("company_id", companyId)
          .order("full_name", { ascending: true }),
      ]);

      if (contactsRes.error) {
        throw contactsRes.error;
      }

      setAccounts(accountRows);
      setStages(stageRows);
      setContacts(contactsRes.data || []);

      if (stageRows.length > 0) {
        setForm((previous) => ({
          ...previous,
          probability:
            previous.probability ||
            String(stageRows[0].default_probability || 0),
        }));
      }
    } catch (error) {
      setMessage(error.message || "Failed to load opportunity setup");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFormChange = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }, []);

  const filteredContacts = contacts.filter((row) => {
    return !form.account_id || row.account_id === form.account_id;
  });

  const handleSave = useCallback(async () => {
    if (
      !companyId ||
      !canCreateRecords ||
      !form.name.trim() ||
      !form.account_id
    ) {
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user?.id) {
        throw new Error("User not found");
      }

      const stageId = stages[0]?.id || null;

      const created = await createOpportunity({
        company_id: companyId,
        owner_id: user.id,
        account_id: form.account_id,
        primary_contact_id: form.primary_contact_id || null,
        name: form.name.trim(),
        amount: Number(form.amount || 0),
        probability: Number(form.probability || 0),
        pipeline_id: stages[0]?.pipeline_id || null,
        stage_id: stageId,
        expected_close_date: form.expected_close_date || null,
        next_step: form.next_step.trim() || null,
        description: form.description.trim() || null,
        forecast_category: form.forecast_category,
        currency_code: form.currency_code,
        status: "open",
      });

      navigate(`/crm/opportunities/${created.id}`);
    } catch (error) {
      setMessage(error.message || "Failed to create opportunity");
    } finally {
      setSaving(false);
    }
  }, [companyId, canCreateRecords, form, stages, navigate]);

  if (loading) {
    return <div className="crm-message">Loading...</div>;
  }

  if (!canCreateRecords) {
    return (
      <div className="crm-message">
        You do not have permission to create opportunities.
      </div>
    );
  }

  return (
    <div>
      <h2 className="crm-page-title">Create Opportunity</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-table-card">
        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Account</label>
            <select
              className="crm-select"
              value={form.account_id}
              onChange={(event) =>
                handleFormChange("account_id", event.target.value)
              }
            >
              <option value="">Select account</option>
              {accounts.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>

          <div className="crm-form-group">
            <label>Primary Contact</label>
            <select
              className="crm-select"
              value={form.primary_contact_id}
              onChange={(event) =>
                handleFormChange("primary_contact_id", event.target.value)
              }
            >
              <option value="">Select contact</option>
              {filteredContacts.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.full_name || "Unnamed contact"}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Opportunity Name</label>
            <input
              className="crm-input"
              value={form.name}
              onChange={(event) => handleFormChange("name", event.target.value)}
            />
          </div>

          <div className="crm-form-group">
            <label>Amount</label>
            <input
              className="crm-input"
              type="number"
              value={form.amount}
              onChange={(event) =>
                handleFormChange("amount", event.target.value)
              }
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Probability</label>
            <input
              className="crm-input"
              type="number"
              value={form.probability}
              onChange={(event) =>
                handleFormChange("probability", event.target.value)
              }
            />
          </div>

          <div className="crm-form-group">
            <label>Expected Close Date</label>
            <input
              className="crm-input"
              type="date"
              value={form.expected_close_date}
              onChange={(event) =>
                handleFormChange("expected_close_date", event.target.value)
              }
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Forecast Category</label>
            <select
              className="crm-select"
              value={form.forecast_category}
              onChange={(event) =>
                handleFormChange("forecast_category", event.target.value)
              }
            >
              <option value="pipeline">Pipeline</option>
              <option value="best_case">Best Case</option>
              <option value="commit">Commit</option>
            </select>
          </div>

          <div className="crm-form-group">
            <label>Next Step</label>
            <input
              className="crm-input"
              value={form.next_step}
              onChange={(event) =>
                handleFormChange("next_step", event.target.value)
              }
            />
          </div>
        </div>

        <div className="crm-form-group">
          <label>Description</label>
          <textarea
            className="crm-textarea"
            rows={5}
            value={form.description}
            onChange={(event) =>
              handleFormChange("description", event.target.value)
            }
          />
        </div>

        <button
          className="crm-primary-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Create Opportunity"}
        </button>
      </div>
    </div>
  );
};

export default CreateOpportunityPage;
